import { api, readinessApi } from "./api";
import { clamp, dollarsToCents } from "./financials";
import type {
  PutReadinessTargetRequest,
  ReadinessResult,
  ReadinessTarget,
  ReadinessTimeline,
} from "./readiness-types";
import type { SavedPropertyVisualToken } from "./property-visual-tokens";

export type SavedPropertyType =
  | "single_family"
  | "condo"
  | "townhome"
  | "multi_family"
  | "manufactured_home";

export type SavedPropertyStatus =
  | "saved"
  | "touring"
  | "offer_made"
  | "under_contract"
  | "closed"
  | "passed";

export type SavedPropertyAffordabilityFit =
  | "comfortable"
  | "stretch"
  | "out_of_range";

export type SavedPropertyCostOverridesSource =
  | "user"
  | "listing"
  | "agent"
  | "quote"
  | "tax_record";

export interface SavedProperty {
  id: string;
  profile_id: string;
  address_line1: string;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country: string;
  formatted_address: string;
  latitude?: number | null;
  longitude?: number | null;
  list_price: number;
  property_type: SavedPropertyType;
  listing_url?: string | null;
  notes?: string | null;
  tags: string[];
  status: SavedPropertyStatus;
  favorite: boolean;
  visual_token: SavedPropertyVisualToken;
  affordability_fit?: SavedPropertyAffordabilityFit | null;
  estimated_property_taxes_monthly_cents?: number | null;
  estimated_homeowners_insurance_monthly_cents?: number | null;
  estimated_hoa_monthly_cents?: number | null;
  cost_overrides_source?: SavedPropertyCostOverridesSource | null;
  view_count: number;
  last_viewed_at?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

export interface ListSavedPropertiesResponse {
  properties: SavedProperty[];
}

export type SavedPropertyReadinessPreview = Pick<
  ReadinessResult,
  "label" | "score" | "plan_viability"
>;

export interface SavedPropertyInput {
  address_line1: string;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  formatted_address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  list_price: number;
  property_type: SavedPropertyType;
  listing_url?: string | null;
  notes?: string | null;
  tags?: string[];
  status?: SavedPropertyStatus;
  favorite?: boolean;
  estimated_property_taxes_monthly_cents?: number | null;
  estimated_homeowners_insurance_monthly_cents?: number | null;
  estimated_hoa_monthly_cents?: number | null;
  cost_overrides_source?: SavedPropertyCostOverridesSource | null;
}

export interface ListSavedPropertiesFilters {
  favoriteOnly?: boolean;
  limit?: number;
  q?: string;
  status?: SavedPropertyStatus;
}

const DEFAULT_READINESS_TARGET_TIMELINE: ReadinessTimeline = "6_12_months";

export const SAVED_PROPERTY_TYPE_OPTIONS: Array<{
  label: string;
  value: SavedPropertyType;
}> = [
  { label: "Single family", value: "single_family" },
  { label: "Condo", value: "condo" },
  { label: "Townhome", value: "townhome" },
  { label: "Multi-family", value: "multi_family" },
  { label: "Manufactured home", value: "manufactured_home" },
];

export const SAVED_PROPERTY_STATUS_OPTIONS: Array<{
  label: string;
  value: SavedPropertyStatus;
}> = [
  { label: "Saved", value: "saved" },
  { label: "Touring", value: "touring" },
  { label: "Offer made", value: "offer_made" },
  { label: "Under contract", value: "under_contract" },
  { label: "Closed", value: "closed" },
  { label: "Passed", value: "passed" },
];

export function formatSavedPropertyType(value: SavedPropertyType) {
  return (
    SAVED_PROPERTY_TYPE_OPTIONS.find((option) => option.value === value)
      ?.label ?? value
  );
}

export function formatSavedPropertyStatus(value: SavedPropertyStatus) {
  return (
    SAVED_PROPERTY_STATUS_OPTIONS.find((option) => option.value === value)
      ?.label ?? value
  );
}

export async function listSavedProperties(
  filters: ListSavedPropertiesFilters = {},
  token: string,
): Promise<ListSavedPropertiesResponse> {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.status) params.set("status", filters.status);
  if (filters.favoriteOnly) params.set("favorite", "true");
  if (typeof filters.limit === "number") {
    params.set("limit", String(filters.limit));
  }

  const path = params.size
    ? `/api/properties?${params.toString()}`
    : "/api/properties";
  return api.get<ListSavedPropertiesResponse>(path, token);
}

export async function createSavedProperty(
  body: SavedPropertyInput,
  token: string,
) {
  return api.post<SavedProperty>("/api/properties", body, token);
}

export async function getSavedProperty(propertyID: string, token: string) {
  return api.get<SavedProperty>(`/api/properties/${propertyID}`, token);
}

export async function updateSavedProperty(
  propertyID: string,
  body: SavedPropertyInput,
  token: string,
) {
  return api.put<SavedProperty>(`/api/properties/${propertyID}`, body, token);
}

export async function deleteSavedProperty(propertyID: string, token: string) {
  return api.delete<void>(`/api/properties/${propertyID}`, token);
}

export async function getActiveReadinessTarget(token: string) {
  const response = await readinessApi.getActive(token);
  return response.target;
}

export function buildSavedPropertyReadinessTargetRequest(
  property: SavedProperty,
  currentTarget: ReadinessTarget | null = null,
): PutReadinessTargetRequest {
  const locationCode = buildSavedPropertyLocationCode(property);
  if (!locationCode) {
    throw new Error(
      "Add a ZIP code or state before setting this property as your readiness target.",
    );
  }

  const request: PutReadinessTargetRequest = {
    kind: "property",
    label: property.formatted_address || property.address_line1,
    location_code: locationCode,
    occupancy: currentTarget?.deal.property.occupancy ?? "primary",
    preferred_product: currentTarget?.deal.preferred_product,
    preferred_term_mo: currentTarget?.deal.preferred_term_mo,
    price: property.list_price,
    property_id: property.id,
    property_type: property.property_type,
    estimated_utilities: currentTarget?.estimated_utilities ?? null,
    estimated_maintenance: currentTarget?.estimated_maintenance ?? null,
    timeline: currentTarget?.timeline ?? DEFAULT_READINESS_TARGET_TIMELINE,
    units: currentTarget?.deal.property.units ?? 1,
  };

  const sellerLenderCredits = currentTarget?.deal.seller_lender_credits_cents;
  if (sellerLenderCredits && sellerLenderCredits > 0) {
    request.seller_lender_credits_cents = sellerLenderCredits;
  }
  const rateOverrideBPS = currentTarget?.deal.rate_override_bps;
  if (rateOverrideBPS && rateOverrideBPS > 0) {
    request.rate_override_bps = rateOverrideBPS;
  }
  const discountPoints = currentTarget?.deal.discount_points;
  if (discountPoints && discountPoints > 0) {
    request.discount_points = discountPoints;
  }
  const downPaymentPlan = buildPropertySavingsGoal(property, currentTarget);
  if ("savings_goal_pct" in downPaymentPlan) {
    request.savings_goal_pct = downPaymentPlan.savings_goal_pct;
  }
  if ("savings_goal_amount" in downPaymentPlan) {
    request.savings_goal_amount = downPaymentPlan.savings_goal_amount;
  }

  if (typeof property.estimated_property_taxes_monthly_cents === "number") {
    request.estimated_taxes = property.estimated_property_taxes_monthly_cents;
  }
  if (
    typeof property.estimated_homeowners_insurance_monthly_cents === "number"
  ) {
    request.estimated_insurance =
      property.estimated_homeowners_insurance_monthly_cents;
  }
  if (typeof property.estimated_hoa_monthly_cents === "number") {
    request.hoa = property.estimated_hoa_monthly_cents;
  }

  return request;
}

export function buildSavedPropertyReadinessPreviewTarget(
  property: SavedProperty,
  currentTarget: ReadinessTarget | null = null,
): ReadinessTarget {
  const downPaymentPlan = buildPropertySavingsGoal(property, currentTarget);
  return {
    archived_at: undefined,
    created_at: new Date().toISOString(),
    deal: {
      preferred_product: "conventional",
      preferred_term_mo: 360,
      property: {
        estimated_taxes:
          property.estimated_property_taxes_monthly_cents ?? undefined,
        estimated_insurance:
          property.estimated_homeowners_insurance_monthly_cents ?? undefined,
        hoa: property.estimated_hoa_monthly_cents ?? undefined,
        location_code: property.postal_code ?? property.state ?? "",
        property_type: property.property_type,
        target_price: dollarsToCents(property.list_price),
        units: 1,
      },
      ...downPaymentPlan,
    },
    id: property.id,
    is_active: false,
    kind: "property",
    label: property.formatted_address || property.address_line1,
    profile_id: property.profile_id,
    property_id: property.id,
    timeline: DEFAULT_READINESS_TARGET_TIMELINE,
  };
}

export async function previewSavedPropertyReadiness(
  property: SavedProperty,
  token: string,
  currentTarget: ReadinessTarget | null = null,
): Promise<ReadinessResult> {
  const response = await readinessApi.preview(
    buildSavedPropertyReadinessPreviewTarget(property, currentTarget),
    token,
  );
  return response.result;
}

export async function previewSavedPropertiesReadiness(
  properties: SavedProperty[],
  token: string,
  currentTarget: ReadinessTarget | null = null,
): Promise<Record<string, SavedPropertyReadinessPreview | null>> {
  const previewEntries = await Promise.all(
    properties.map(async (property) => {
      try {
        const result = await previewSavedPropertyReadiness(
          property,
          token,
          currentTarget,
        );
        return [
          property.id,
          {
            label: result.label,
            plan_viability: result.plan_viability,
            score: result.score,
          },
        ] as const;
      } catch {
        return [property.id, null] as const;
      }
    }),
  );

  return Object.fromEntries(previewEntries);
}

export async function setSavedPropertyAsReadinessTarget(
  property: SavedProperty,
  currentTarget: ReadinessTarget | null,
  token: string,
) {
  return readinessApi.setTarget(
    buildSavedPropertyReadinessTargetRequest(property, currentTarget),
    token,
  );
}

function buildSavedPropertyLocationCode(property: SavedProperty) {
  const postalCode = property.postal_code?.trim();
  if (postalCode) {
    return postalCode;
  }

  const state = property.state?.trim().toUpperCase();
  if (state) {
    return state;
  }

  return "";
}

function buildPropertySavingsGoal(
  property: SavedProperty,
  currentTarget: ReadinessTarget | null,
): Pick<PutReadinessTargetRequest, "savings_goal_amount" | "savings_goal_pct"> {
  const explicitPct = currentTarget?.deal.savings_goal_pct;
  if (typeof explicitPct === "number" && Number.isFinite(explicitPct)) {
    const savingsGoalPct = clamp(explicitPct, 0, 1);
    return {
      savings_goal_amount: Math.round(property.list_price * savingsGoalPct),
      savings_goal_pct: savingsGoalPct,
    };
  }

  const explicitAmount = currentTarget?.deal.savings_goal_amount;
  if (
    typeof explicitAmount === "number" &&
    Number.isFinite(explicitAmount) &&
    explicitAmount > 0
  ) {
    const savingsGoalAmount = Math.min(
      Math.round(explicitAmount),
      property.list_price,
    );
    return {
      savings_goal_amount: savingsGoalAmount,
      savings_goal_pct:
        property.list_price > 0
          ? clamp(savingsGoalAmount / property.list_price, 0, 1)
          : 0,
    };
  }

  return {};
}
